import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
    Input,
    Typography,
} from "@material-tailwind/react";

import {
    XMarkIcon,
    CalendarDaysIcon,
} from "@heroicons/react/24/solid";

import Select from "react-select";
import { useForm, Controller } from "react-hook-form";
import { assignAppointment, assignAppointmentToSalesTeam, getSalesManagers, getSalesMembers } from "@/store/slices/appointmentSlice";
import { useDispatch, useSelector } from "react-redux";
import Toaster from "@/components/Toaster";
import { fetchSalesManager, fetchSalesTeamMember, getSalesMemberById } from "@/store/slices/rfqSlice";
import { useEffect } from "react";
import moment from "moment";
import ShowLoader from "@/components/Loader";
import { useTranslation } from "react-i18next";

const customSelectStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: "44px",
        borderRadius: "8px",
        borderColor: "#E5E7EB",
        boxShadow: "none",
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
};

export default function AssignTeamModal({
    open,
    onClose,
    appointment,
    onSubmited
}) {

    const { t } = useTranslation('appointment');
    const { t: ct } = useTranslation("common");

    const dispatch = useDispatch();
    const { salesMembers } = useSelector((state) => state.appointment);

    useEffect(() => {
        if (!appointment?.manager_id) return;
        dispatch(getSalesMembers({ manager_id: appointment?.manager_id }));
    }, [dispatch, appointment]);


    const {
        register,
        handleSubmit,
        control,
        watch,
        setValue,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            sales_rep_id: null,
        },
    });


    const {
        loading,
    } = useSelector(
        (state) => state.appointment
    );


    const onSubmit = async (data) => {

        const payload = {
            id: appointment?.id,
            sales_rep_id: data?.sales_rep_id?.value ?? data?.sales_rep_id?.user_id,
        };

        try {

            const response = await dispatch(
                assignAppointmentToSalesTeam?.(payload)
            ).unwrap();

            if (response?.status) {
                Toaster.success(response?.message || t("assignSuccess"));
                reset();
                onSubmited();
            } else {
                Toaster.error(response?.message || t("assignFailed"));
            }

        } catch (error) {
            Toaster.error(error?.message || t("assignFailed"));
        }
    };

    const close = () => {
        reset();
        onClose();
    }

    return (
        <Dialog
            open={open}
            handler={close}
            size="sm"
            className="rounded-xl p-5"
        >
            {loading && (<ShowLoader />)}

            <DialogHeader className="border-b flex justify-between items-center">
                <Typography
                    variant="h4"
                    className="font-semibold text-black"
                >
                    {t("title3")}
                </Typography>

                <XMarkIcon
                    onClick={close}
                    className="h-6 w-6 cursor-pointer text-gray-700"
                />
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogBody className="max-h-[70vh] overflow-y-auto pb-20">
                    <div className="space-y-5">
                        {/* Sales Member */}
                        <div>
                            <Typography
                                variant="small"
                                className="mb-2 font-medium"
                            >
                                {t("salesMemberLabel")}
                            </Typography>

                            <Controller
                                control={control}
                                name="sales_rep_id"
                                rules={{
                                    required: t("salesMemberRequired"),
                                }}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={salesMembers}
                                        placeholder={t("salesMemberPlaceholder")}
                                        styles={customSelectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                )}
                            />

                            {errors.sales_rep_id && (
                                <p className="mt-1 text-sm text-red-500">
                                    {errors.sales_rep_id.message}
                                </p>
                            )}
                        </div>
                    </div>
                </DialogBody>
                <DialogFooter className="border-t flex justify-end gap-3">
                    <Button
                        variant="outlined"
                        color="gray"
                        onClick={close}
                    >
                        {ct("notNowButton")}
                    </Button>

                    <Button
                        type="submit"
                        className="bg-amber-500 shadow-none hover:shadow-none"
                    >
                        {ct("assignButton")}
                    </Button>
                </DialogFooter>
            </form>
        </Dialog>
    );
}